// Dashboard → Aliados → «Estados de cuenta»: todos los meses cerrados de todas
// las armerías, con lo que falta por cobrar, vencidos, en proceso. Desde aquí
// el staff cobra por ACH (si la armería vinculó banco), marca pagos por otro
// medio, condona, añade ajustes y recalcula un mes.
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import axios from 'axios';
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
import firebaseApp from '../../utils/firebaseConfig';
import {
  money,
  toCents,
  monthLabel,
  settlementStatus,
  formatDueDate,
  todayIso,
  currentMonthKey,
  recentMonthKeys,
} from '../../utils/partners';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const apiError = (err, fallback) => (err.response && err.response.data && err.response.data.error) || fallback;

// Lo que falta por pagar (misma fórmula que functions/partnersBilling.js)
function amountDue(s) {
  if (s.status === 'paid' || s.status === 'waived') return 0;
  const total = Number(s.totalCents) || 0;
  const adj = (s.adjustments || []).reduce((a, x) => a + (Number(x.cents) || 0), 0);
  const paid = (s.payments || []).filter((p) => p.status === 'succeeded').reduce((a, p) => a + (Number(p.cents) || 0), 0);
  return Math.max(0, total + adj - paid);
}

const FILTERS = [
  { key: 'pending', label: 'Por cobrar' },
  { key: 'overdue', label: 'Vencidos' },
  { key: 'processing', label: 'En proceso' },
  { key: 'paid', label: 'Pagados' },
  { key: 'all', label: 'Todos' },
];

export default function SettlementsAdmin({ partners, canEdit, onOpenDetail }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [payDialog, setPayDialog] = useState(null); // { s, note }
  const [adjDialog, setAdjDialog] = useState(null); // { s, amount, note }
  const [closeDialog, setCloseDialog] = useState(false);
  const [closeMonth, setCloseMonth] = useState(recentMonthKeys(2)[1]);
  const [closePartner, setClosePartner] = useState('');
  const today = todayIso();

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'partner_settlements'), orderBy('month', 'desc')));
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data(), due: amountDue(d.data()) })));
    } catch (e) {
      console.error(e);
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const partnerOf = (s) => partners.find((p) => p.id === s.partnerId);

  const visible = useMemo(() => rows.filter((s) => {
    const st = settlementStatus(s, today);
    if (filter === 'pending') return ['closed', 'failed', 'disputed'].includes(s.status) && s.due > 0;
    if (filter === 'overdue') return st.overdue;
    if (filter === 'processing') return s.status === 'processing';
    if (filter === 'paid') return s.status === 'paid' || s.status === 'waived';
    return true;
  }), [rows, filter, today]);

  const totals = useMemo(() => ({
    pending: rows.filter((s) => ['closed', 'failed', 'disputed'].includes(s.status)).reduce((a, s) => a + s.due, 0),
    overdue: rows.filter((s) => settlementStatus(s, today).overdue).reduce((a, s) => a + s.due, 0),
    processing: rows.filter((s) => s.status === 'processing').reduce((a, s) => a + (Number(s.totalCents) || 0), 0),
    paidMonth: rows.filter((s) => s.status === 'paid' && s.paidAt && String(s.paidAt.toDate ? s.paidAt.toDate().toISOString() : s.paidAt).startsWith(currentMonthKey())).reduce((a, s) => a + (Number(s.totalCents) || 0), 0),
  }), [rows, today]);

  const run = async (label, fn, okMsg) => {
    setBusy(label);
    setError('');
    setNotice('');
    try {
      await fn();
      if (okMsg) setNotice(okMsg);
      await load();
    } catch (e) {
      console.error(e);
      setError(apiError(e, e.message || 'No se pudo completar la acción.'));
    }
    setBusy('');
  };

  const charge = (s) => {
    if (!window.confirm(`¿Iniciar un débito ACH de ${money(s.due)} a ${s.partnerName} por ${monthLabel(s.month)}?`)) return;
    run(`charge-${s.id}`, () => axios.post(`${FUNCTIONS_BASE}/adminChargeSettlement`, { settlementId: s.id }), `Débito de ${money(s.due)} iniciado; el banco lo confirma en 3–5 días hábiles.`);
  };

  const markPaid = () => {
    const { s, note } = payDialog;
    setPayDialog(null);
    run(`paid-${s.id}`, () => updateDoc(doc(db, 'partner_settlements', s.id), {
      status: 'paid', paidAt: serverTimestamp(), paidVia: 'manual', paidNote: note.trim(), amountDueCents: 0, note: note.trim() || s.note || '', updatedAt: serverTimestamp(),
    }), `${monthLabel(s.month)} de ${s.partnerName} marcado como pagado.`);
  };

  const waive = (s) => {
    if (!window.confirm(`¿Condonar el balance de ${money(s.due)} de ${s.partnerName} (${monthLabel(s.month)})? No se cobrará.`)) return;
    run(`waive-${s.id}`, () => updateDoc(doc(db, 'partner_settlements', s.id), { status: 'waived', waivedAt: serverTimestamp(), amountDueCents: 0, updatedAt: serverTimestamp() }), 'Balance condonado.');
  };

  const addAdjustment = () => {
    const { s, amount, note } = adjDialog;
    const cents = toCents(amount) * (String(amount).trim().startsWith('-') ? -1 : 1);
    if (!cents || !note.trim()) { setError('Escribe el monto (negativo para un crédito) y la nota del ajuste.'); return; }
    setAdjDialog(null);
    const adjustments = [...(s.adjustments || []), { cents, note: note.trim(), at: new Date().toISOString(), by: 'staff' }];
    const due = amountDue({ ...s, adjustments });
    run(`adj-${s.id}`, () => updateDoc(doc(db, 'partner_settlements', s.id), {
      adjustments: arrayUnion(adjustments[adjustments.length - 1]),
      amountDueCents: due,
      ...(due === 0 && ['closed', 'failed'].includes(s.status) ? { status: 'paid', paidAt: serverTimestamp(), paidVia: 'adjustment' } : {}),
      updatedAt: serverTimestamp(),
    }), 'Ajuste guardado.');
  };

  const recalc = (s) => {
    if (!window.confirm(`¿Recalcular ${monthLabel(s.month)} de ${s.partnerName} con las asistencias actuales? Se conservan pagos y ajustes.`)) return;
    run(`recalc-${s.id}`, () => axios.post(`${FUNCTIONS_BASE}/runClosePartnerMonthNow`, { month: s.month, partnerId: s.partnerId, force: true, notify: false }), 'Mes recalculado.');
  };

  const reopen = (s) => {
    if (!window.confirm(`¿Volver a poner ${monthLabel(s.month)} de ${s.partnerName} como pendiente de pago?`)) return;
    run(`reopen-${s.id}`, () => updateDoc(doc(db, 'partner_settlements', s.id), { status: 'closed', amountDueCents: amountDue({ ...s, status: 'closed' }), paidAt: null, paidVia: null, updatedAt: serverTimestamp() }), 'Estado de cuenta reabierto.');
  };

  const runClose = () => {
    setCloseDialog(false);
    run('close', () => axios.post(`${FUNCTIONS_BASE}/runClosePartnerMonthNow`, { month: closeMonth, partnerId: closePartner || null, notify: true }), `Cierre de ${monthLabel(closeMonth)} ejecutado; a las armerías con balance les llegó el estado de cuenta.`);
  };

  const runDaily = () => {
    if (!window.confirm('¿Correr ahora el trabajo diario? Debita automáticamente lo vencido de las armerías con débito automático y manda los recordatorios que toquen.')) return;
    run('daily', () => axios.post(`${FUNCTIONS_BASE}/runPartnerStatementsDailyNow`, {}), 'Trabajo diario ejecutado.');
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          ['Por cobrar', totals.pending, 'warning.main'],
          ['Vencido', totals.overdue, 'error.main'],
          ['En proceso (ACH)', totals.processing, 'info.main'],
          ['Cobrado este mes', totals.paidMonth, 'success.main'],
        ].map(([l, v, c]) => (
          <Grid item xs={6} md={3} key={l}>
            <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
              <Typography variant="h5" sx={{ color: c }}>{money(v)}</Typography>
              <Typography variant="caption" color="text.secondary">{l}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {FILTERS.map((f) => <Chip key={f.key} label={f.label} clickable color={filter === f.key ? 'primary' : 'default'} variant={filter === f.key ? 'filled' : 'outlined'} onClick={() => setFilter(f.key)} />)}
          </Stack>
          {canEdit && (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => setCloseDialog(true)} disabled={Boolean(busy)} startIcon={<Iconify icon="eva:lock-outline" />}>Cerrar un mes ahora</Button>
              <Button size="small" variant="outlined" onClick={runDaily} disabled={Boolean(busy)} startIcon={<Iconify icon="eva:flash-outline" />}>Débitos y recordatorios</Button>
              <IconButton size="small" onClick={load} disabled={Boolean(busy)}><Iconify icon="eva:refresh-outline" /></IconButton>
            </Stack>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          El cierre corre solo el día 1 a las 2:00 am (mes anterior) y el débito automático / recordatorios todos los días a las 9:00 am. Los meses cerrados quedan congelados: si cambias una asistencia después, usa «Recalcular».
        </Typography>

        <Scrollbar>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mes</TableCell>
                  <TableCell>Armería</TableCell>
                  <TableCell>Vence</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Pendiente</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={7}><CircularProgress size={20} /></TableCell></TableRow>}
                {!loading && visible.length === 0 && <TableRow><TableCell colSpan={7}><Typography color="text.secondary" sx={{ p: 1 }}>Nada en este filtro.</Typography></TableCell></TableRow>}
                {visible.map((s) => {
                  const st = settlementStatus(s, today);
                  const p = partnerOf(s);
                  const bankOk = p && p.bankStatus === 'verified';
                  const chargeable = ['closed', 'failed'].includes(s.status) && s.due > 0;
                  const rowBusy = busy.endsWith(s.id);
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{monthLabel(s.month)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{s.partnerName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p ? (bankOk ? `Banco verificado${p.autoDebit ? ' · débito automático' : ''}` : (p.bankStatus ? 'Banco pendiente' : 'Sin banco')) : ''}
                          {s.unmarkedCompleted ? ` · ${s.unmarkedCompleted} sin «Showed»` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.dueDate ? formatDueDate(s.dueDate) : '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={st.label} color={st.color} variant={st.overdue ? 'filled' : 'outlined'} />
                        {s.lastChargeError && s.status === 'failed' && <Typography variant="caption" color="error" sx={{ display: 'block' }}>{s.lastChargeError}</Typography>}
                        {s.status === 'paid' && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.paidVia === 'ach' ? 'ACH' : (s.paidNote || s.note || 'manual')}</Typography>}
                      </TableCell>
                      <TableCell align="right">{money(s.totalCents)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: s.due > 0 ? 'error.main' : undefined }}>{money(s.due)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {rowBusy ? <CircularProgress size={18} /> : (
                          <>
                            <Tooltip title="Ver detalle del mes"><IconButton size="small" onClick={() => onOpenDetail(s.partnerId, s.month)}><Iconify icon="eva:file-text-outline" /></IconButton></Tooltip>
                            {canEdit && chargeable && (
                              <Tooltip title={bankOk ? 'Cobrar por ACH' : 'La armería no ha vinculado banco'}><span><IconButton size="small" color="primary" disabled={!bankOk || Boolean(busy)} onClick={() => charge(s)}><Iconify icon="mdi:bank-transfer" /></IconButton></span></Tooltip>
                            )}
                            {canEdit && chargeable && <Tooltip title="Marcar pagado por otro medio"><IconButton size="small" color="success" disabled={Boolean(busy)} onClick={() => setPayDialog({ s, note: '' })}><Iconify icon="eva:checkmark-circle-2-outline" /></IconButton></Tooltip>}
                            {canEdit && ['closed', 'failed'].includes(s.status) && <Tooltip title="Ajuste / crédito"><IconButton size="small" disabled={Boolean(busy)} onClick={() => setAdjDialog({ s, amount: '', note: '' })}><Iconify icon="eva:edit-2-outline" /></IconButton></Tooltip>}
                            {canEdit && ['closed', 'failed'].includes(s.status) && <Tooltip title="Recalcular con las asistencias actuales"><IconButton size="small" disabled={Boolean(busy)} onClick={() => recalc(s)}><Iconify icon="eva:refresh-outline" /></IconButton></Tooltip>}
                            {canEdit && chargeable && <Tooltip title="Condonar"><IconButton size="small" disabled={Boolean(busy)} onClick={() => waive(s)}><Iconify icon="eva:slash-outline" /></IconButton></Tooltip>}
                            {canEdit && (s.status === 'paid' || s.status === 'waived') && s.paidVia !== 'ach' && <Tooltip title="Volver a pendiente"><IconButton size="small" disabled={Boolean(busy)} onClick={() => reopen(s)}><Iconify icon="eva:undo-outline" /></IconButton></Tooltip>}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>

      <Dialog open={Boolean(payDialog)} onClose={() => setPayDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Marcar como pagado</DialogTitle>
        <DialogContent>
          {payDialog && <Typography variant="body2" sx={{ mb: 2 }}>{payDialog.s.partnerName} · {monthLabel(payDialog.s.month)} · <strong>{money(payDialog.s.due)}</strong></Typography>}
          <TextField autoFocus fullWidth label="Medio y referencia" placeholder="Ej: cheque #123, ATH Móvil, efectivo" value={payDialog ? payDialog.note : ''} onChange={(e) => setPayDialog({ ...payDialog, note: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={markPaid}>Marcar pagado</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(adjDialog)} onClose={() => setAdjDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Ajuste al estado de cuenta</DialogTitle>
        <DialogContent>
          {adjDialog && <Typography variant="body2" sx={{ mb: 2 }}>{adjDialog.s.partnerName} · {monthLabel(adjDialog.s.month)} · pendiente {money(adjDialog.s.due)}</Typography>}
          <Stack spacing={2}>
            <TextField fullWidth label="Monto ($)" placeholder="-20.00 para un crédito" value={adjDialog ? adjDialog.amount : ''} onChange={(e) => setAdjDialog({ ...adjDialog, amount: e.target.value })} helperText="Positivo suma al balance; negativo lo reduce (crédito)" />
            <TextField fullWidth label="Nota (se ve en el portal y en el email)" value={adjDialog ? adjDialog.note : ''} onChange={(e) => setAdjDialog({ ...adjDialog, note: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={addAdjustment}>Guardar ajuste</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeDialog} onClose={() => setCloseDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cerrar un mes ahora</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Congela el estado de cuenta del mes escogido y le envía el email a cada armería con balance. Los meses ya cerrados no se tocan (usa «Recalcular» en la fila).
          </Typography>
          <Stack spacing={2}>
            <TextField select fullWidth label="Mes" value={closeMonth} onChange={(e) => setCloseMonth(e.target.value)}>
              {recentMonthKeys(12).map((k) => <MenuItem key={k} value={k}>{monthLabel(k)}{k === currentMonthKey() ? ' (en curso)' : ''}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Armería" value={closePartner} onChange={(e) => setClosePartner(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {partners.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={runClose}>Cerrar {monthLabel(closeMonth)}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

SettlementsAdmin.propTypes = {
  partners: PropTypes.array.isRequired,
  canEdit: PropTypes.bool,
  onOpenDetail: PropTypes.func.isRequired,
};
